import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import { KOMPONEN_DATA, PIPA_DATA, KomponenTeknik, PipaFeature } from "./gisData";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "../../../assets/style/teknik/GISTeknikMap.css"
import html2canvas from "html2canvas";
import Swal from "sweetalert2";

export interface KategoriIcon { html: string; label: string; }
export interface PipeStyle { color: string; weight: number; label: string; }

type BasemapKey = "osm" | "google_sat" | "google_hybrid" | "google_map" | "google_terrain" | "esri";
type Mode = "" | "marker" | "pipa" | "jaringan" | "measure" | "streetview";

interface Props {
  center?: [number, number];
  zoom?: number;
  userName?: string;
  googleMapsApiKey?: string; // butuh untuk Street View
}

const KATEGORI_ICONS: Record<string, KategoriIcon> = {
  "Tempat  Cuci Pipa": { html: "📍", label: "Tempat  Cuci Pipa" },
  "KTR Pdam": { html: "🏦", label: "KTR Pdam" },
  Dop: { html: "🔵", label: "Dop" },
  Intake: { html: "💧", label: "Intake" },
  IPA: { html: "🏭", label: "IPA" },
  "Meter Induk": { html: "🚰", label: "Meter Induk" },
  Pompa: { html: "⚙️", label: "Pompa" },
  Reduser: { html: "🔻", label: "Reduser" },
  Reservoar: { html: "🔘", label: "Reservoar" },
  RPR: { html: "📏", label: "RPR" },
  SPL: { html: "📦", label: "SPL" },
  "Stop Kran": { html: "⛔", label: "Stop Kran" },
  "Sumber Air & Pompa": { html: "💦", label: "Sumber Air & Pompa" },
  "Tee Y": { html: "🧪", label: "Tee Y" },
  Testek: { html: "🔀", label: "Testek" },
  manhole: { html: "📍", label: "manhole" },
  Beugel: { html: "🔺", label: "Beugel" },
};

const PIPE_COLORS: Record<string, PipeStyle> = {
  "pipa 2 1/2": { color: "#0798f2", weight: 3, label: "pipa 2 1/2" },
  "Pipa 350": { color: "black", weight: 6.5, label: "Pipa 350" },
  "Pipa 300": { color: "brown", weight: 6, label: "Pipa 300" },
  "Pipa 250": { color: "purple", weight: 5.5, label: "Pipa 250" },
  "Pipa 200": { color: "green", weight: 5, label: "Pipa 200" },
  "Pipa 150": { color: "blue", weight: 4.5, label: "Pipa 150" },
  "Pipa 100": { color: "red", weight: 4, label: "Pipa 100" },
  "Pipa 75": { color: "magenta", weight: 3.5, label: "Pipa 75" },
  "Pipa 50": { color: "cyan", weight: 3, label: "Pipa 50" },
  "Pipa 40": { color: "yellow", weight: 2.5, label: "Pipa 40" },
  "Pipa 25": { color: "orange", weight: 2, label: "Pipa 25" },
  "Pipa 3/4": { color: "#aa7b55", weight: 2, label: "Pipa 3/4" },
  "Pipa Pamsimas": { color: "gray", weight: 1.5, label: "Pipa Pamsimas" },
  "Pipa 100 Plan": { color: "#e8d930", weight: 3, label: "Pipa 100 Plan" },
};

const BASEMAPS: Record<BasemapKey, { label: string; url: string; opts: L.TileLayerOptions }> = {
  osm: { label: "OSM", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", opts: { attribution: "© OSM", maxZoom: 21 } },
  google_map: { label: "Google Map", url: "https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", opts: { subdomains: ["mt0","mt1","mt2","mt3"], maxZoom: 21 } as any },
  google_sat: { label: "Satelit", url: "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", opts: { subdomains: ["mt0","mt1","mt2","mt3"], maxZoom: 21 } as any },
  google_hybrid: { label: "Hybrid", url: "https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", opts: { subdomains: ["mt0","mt1","mt2","mt3"], maxZoom: 21 } as any },
  google_terrain: { label: "Terrain", url: "https://{s}.google.com/vt/lyrs=t&x={x}&y={y}&z={z}", opts: { subdomains: ["mt0","mt1","mt2","mt3"], maxZoom: 21 } as any },
  esri: { label: "Esri Sat", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", opts: { maxZoom: 21 } },
};

const calcDistance = (latlngs: L.LatLng[]) => {
  let total = 0;
  const toRad = (v:number) => v*Math.PI/180;
  for(let i=0;i<latlngs.length-1;i++){
    const p1=latlngs[i], p2=latlngs[i+1];
    const R=6371000;
    const dLat=toRad(p2.lat-p1.lat), dLng=toRad(p2.lng-p1.lng);
    const a=Math.sin(dLat/2)**2 + Math.cos(toRad(p1.lat))*Math.cos(toRad(p2.lat))*Math.sin(dLng/2)**2;
    total+=R*2*Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }
  return total;
};

const TeknikPetaJaringan: React.FC<Props> = ({
  center = [0.481634, 121.954445],
  zoom = 12,
  userName = "Alan Zulfikar Waksito",
  googleMapsApiKey = "AIzaSyD6kCeCtG8L0LcdXqH2zBkFsPX28fmXO5U",
//   <script src="https://maps.googleapis.com/maps/api/js?key=&amp;libraries=streetView"></script>
}) => {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.MarkerClusterGroup | null>(null);
  const pipaGroupRef = useRef<L.FeatureGroup | null>(null);
  const markerLayersRef = useRef<Record<string, L.LayerGroup>>({});
  const basemapLayerRef = useRef<L.TileLayer | null>(null);
  const tempPolylineRef = useRef<L.Polyline | null>(null);
  const editableLayerRef = useRef<any>(null);
  const streetViewLayerRef = useRef<L.LayerGroup | null>(null);
  const streetViewPanoramaRef = useRef<any>(null);

  // FIX ADD PIPA: pakai ref biar tidak stale closure
  const newLineRef = useRef<L.LatLng[]>([]);
  const modeRef = useRef<Mode>("");

  const [komponenList, setKomponenList] = useState<KomponenTeknik[]>(KOMPONEN_DATA);
  const [pipaFeatures, setPipaFeatures] = useState<PipaFeature[]>(PIPA_DATA.features);

  const [mode, setMode] = useState<Mode>("");
  const [showLabels, setShowLabels] = useState(true);
  const [markerVisibility, setMarkerVisibility] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    Object.keys(KATEGORI_ICONS).forEach(k => (init[k] = true));
    return init;
  });
  const [pipeVisibility, setPipeVisibility] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    Object.keys(PIPE_COLORS).forEach(k => (init[k] = true));
    return init;
  });
  const [currentBasemap, setCurrentBasemap] = useState<BasemapKey>("google_map");
  const [stats, setStats] = useState({ marker: 0, pipa: 0 });
  const [loading, setLoading] = useState(true);
  const [newLine, setNewLine] = useState<L.LatLng[]>([]);
  const [measurePoints, setMeasurePoints] = useState<L.LatLng[]>([]);
  const [modeInfo, setModeInfo] = useState("");
  const [showExport, setShowExport] = useState(false);
  const [showAddMarkerModal, setShowAddMarkerModal] = useState(false);
  const [showEditPipaModal, setShowEditPipaModal] = useState(false);
  const [editPipaForm, setEditPipaForm] = useState({ id: "", jenis: "Pipa 100", keterangan: "" });
  const [addMarkerForm, setAddMarkerForm] = useState({ lat: 0, lng: 0, kategori: "Dop", keterangan: "" });
  const [streetViewActive, setStreetViewActive] = useState(false);
  const [showStreetView, setShowStreetView] = useState(false);

  // sync ref dengan state mode
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { newLineRef.current = newLine; }, [newLine]);

  const updateStats = useCallback(() => {
    let m = 0; markersRef.current?.eachLayer(() => m++);
    let p = 0; pipaGroupRef.current?.eachLayer(() => p++);
    setStats({ marker: m, pipa: p });
  }, []);

  const renderMarkers = useCallback((data: KomponenTeknik[]) => {
    if (!markersRef.current) return;
    markersRef.current.clearLayers();
    markerLayersRef.current = {};
    data.forEach(item => {
      if (!item.lat || !item.lng) return;
      const icon = L.divIcon({
        html: KATEGORI_ICONS[item.kategori]?.html || "📍",
        className: "custom-div-icon",
        iconSize: [24,24],
        iconAnchor: [12,12],
      });
      const marker = L.marker([item.lat, item.lng], { icon } as any);
      marker.bindTooltip(item.kategori, { permanent: true, direction: "top", className: "label-nama", offset: [0,-10], opacity: showLabels ? 1 : 0 } as any);
      marker.on("click", () => {
        if (modeRef.current !== "") return;
        Swal.fire({ title: `Edit ${item.kategori}`, input: "text", inputValue: item.keterangan || "", showCancelButton: true }).then(res => {
          if (res.isConfirmed) {
            setKomponenList(prev => prev.map(k => k.id === item.id ? { ...k, keterangan: res.value || "" } : k));
          }
        });
      });
      if (!markerLayersRef.current[item.kategori]) markerLayersRef.current[item.kategori] = L.layerGroup();
      markerLayersRef.current[item.kategori].addLayer(marker);
    });
    markersRef.current.clearLayers();
    Object.entries(markerLayersRef.current).forEach(([kat, layer]) => {
      if (markerVisibility[kat] !== false) markersRef.current!.addLayer(layer);
    });
    if (mapRef.current && !mapRef.current.hasLayer(markersRef.current)) mapRef.current.addLayer(markersRef.current);
    updateStats();
  }, [markerVisibility, showLabels, updateStats]);

  const renderPipa = useCallback((features: PipaFeature[]) => {
    if (!pipaGroupRef.current) return;
    pipaGroupRef.current.clearLayers();
    features.forEach(feature => {
      if (!feature.geometry || feature.geometry.type !== "LineString") return;
      const jenis = feature.properties.jenis_pipa;
      if (pipeVisibility[jenis] === false) return;
      const coords = feature.geometry.coordinates.map(c => [parseFloat(String(c[1])), parseFloat(String(c[0]))] as [number, number]);
      if (coords.length < 2) return;
      const style = PIPE_COLORS[jenis] || { color: "black", weight: 3, label: jenis };
      const poly = L.polyline(coords as any, { color: style.color, weight: style.weight, opacity: 0.9 } as any) as any;
      poly._pipaId = feature.properties.id;
      poly._jenisPipa = jenis;
      poly._keterangan = feature.properties.keterangan;
      poly.on("click", () => {
        if (modeRef.current === "jaringan") {
          if (editableLayerRef.current && editableLayerRef.current !== poly) {
            try { editableLayerRef.current.disableEdit?.(); } catch {}
            const prev = PIPE_COLORS[editableLayerRef.current._jenisPipa] || { weight: 3 } as any;
            editableLayerRef.current.setStyle({ weight: prev.weight, dashArray: null });
          }
          editableLayerRef.current = poly;
          try { poly.enableEdit?.(); } catch {}
          poly.setStyle({ weight: style.weight + 2, dashArray: "5,5" });
          setEditPipaForm({ id: String(poly._pipaId), jenis: poly._jenisPipa, keterangan: poly._keterangan || "" });
        } else if (modeRef.current === "") {
          setEditPipaForm({ id: String(feature.properties.id), jenis: feature.properties.jenis_pipa, keterangan: feature.properties.keterangan || "" });
          setShowEditPipaModal(true);
        }
      });
      pipaGroupRef.current!.addLayer(poly);
    });
    updateStats();
  }, [pipeVisibility, updateStats]);

  // ===== STREET VIEW LOGIC (dari referensi HTML) =====
  const loadGoogleMaps = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if ((window as any).google?.maps) return resolve();
      if (!googleMapsApiKey) {
        // kalau tidak ada key, tetap resolve tapi StreetView akan warning
        console.warn("Google Maps API Key kosong - StreetView butuh key");
        return resolve();
      }
      const existing = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve());
        return;
      }
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.head.appendChild(script);
    });
  }, [googleMapsApiKey]);

  const initStreetView = useCallback(async () => {
    try {
      await loadGoogleMaps();
      const g = (window as any).google;
      if (!g?.maps) return;
      const el = document.getElementById("street-view");
      if (!el) return;
      streetViewPanoramaRef.current = new g.maps.StreetViewPanorama(el, {
        position: { lat: center[0], lng: center[1] },
        pov: { heading: 165, pitch: 0 },
        zoom: 1,
        visible: true,
        addressControl: false,
      });
    } catch (e) {
      console.error("Gagal load Google Maps", e);
    }
  }, [center, loadGoogleMaps]);

  const openStreetView = useCallback(async (lat: number, lng: number) => {
    await loadGoogleMaps();
    const g = (window as any).google;
    if (!g?.maps || !streetViewPanoramaRef.current) {
      Swal.fire({ icon: "warning", title: "Street View Belum Siap", text: "Google Maps belum siap atau API Key belum di-set", confirmButtonColor: "#1e88e5" });
      return;
    }
    const pos = new g.maps.LatLng(lat, lng);
    const svService = new g.maps.StreetViewService();
    svService.getPanorama({ location: pos, radius: 50 }, (data: any, status: string) => {
      if (status === "OK") {
        setShowStreetView(true);
        streetViewPanoramaRef.current.setPosition(pos);
        streetViewLayerRef.current?.clearLayers();
        L.marker([lat, lng], {
          icon: L.divIcon({ html: '<i class="bi bi-geo-alt-fill" style="color:#EA4335; font-size:24px;"></i>', className: "street-view-marker", iconSize: [24,24] })
        }).addTo(streetViewLayerRef.current!);
      } else {
        Swal.fire({ icon: "info", title: "Street View Tidak Tersedia", text: "Street View tidak tersedia di lokasi ini.", confirmButtonColor: "#1e88e5" });
      }
    });
  }, [loadGoogleMaps]);

  const closeStreetView = () => {
    setShowStreetView(false);
  };

  const toggleStreetView = () => {
    if (!streetViewActive) {
      setStreetViewActive(true);
      setMode("streetview");
      modeRef.current = "streetview";
      setModeInfo("Street View Mode - Klik di peta untuk melihat Street View");
      if (mapRef.current) mapRef.current.getContainer().style.cursor = "crosshair";
      if (!streetViewPanoramaRef.current) initStreetView();
    } else {
      setStreetViewActive(false);
      resetMode();
      closeStreetView();
    }
  };

  // INIT MAP - FIX DOUBLE CLICK & CLOSURE
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    const map = L.map(mapDivRef.current, { 
      center: center as any, 
      zoom, 
      minZoom: 10, 
      maxZoom: 21, 
      zoomControl: false, 
      editable: true,
      doubleClickZoom: false, // PENTING untuk add pipa double-click
    } as any);
    mapRef.current = map;
    basemapLayerRef.current = L.tileLayer(BASEMAPS[currentBasemap].url, BASEMAPS[currentBasemap].opts).addTo(map);
    markersRef.current = L.markerClusterGroup({ disableClusteringAtZoom: 16, maxClusterRadius: 50 } as any);
    pipaGroupRef.current = L.featureGroup().addTo(map);
    streetViewLayerRef.current = L.layerGroup().addTo(map);
    tempPolylineRef.current = L.polyline([], { color: "red", weight: 3, dashArray: "6 6" }).addTo(map);

    // SINGLE CLICK HANDLER - pakai ref biar tidak stale
    map.on("click", (e: L.LeafletMouseEvent) => {
      const currentMode = modeRef.current;
      if (currentMode === "streetview") {
        openStreetView(e.latlng.lat, e.latlng.lng);
        return;
      }
      if (currentMode === "marker") {
        setAddMarkerForm({ lat: e.latlng.lat, lng: e.latlng.lng, kategori: "Dop", keterangan: "" });
        setShowAddMarkerModal(true);
        return;
      }
      if (currentMode === "pipa") {
        const next = [...newLineRef.current, e.latlng];
        newLineRef.current = next;
        setNewLine(next);
        tempPolylineRef.current?.setLatLngs(next as any);
        // tambahkan marker kecil tiap titik biar user tau
        L.circleMarker(e.latlng, { radius: 5, color: "#ff0000", fillColor: "#ff0000", fillOpacity: 1 }).addTo(map);
        return;
      }
      if (currentMode === "measure") {
        setMeasurePoints(prev => [...prev, e.latlng]);
        return;
      }
    });

    // DOUBLE CLICK FINISH PIPA
    map.on("dblclick", (e: L.LeafletMouseEvent) => {
      const currentMode = modeRef.current;
      if (currentMode === "pipa") {
        L.DomEvent.stop(e); // cegah zoom
        if (newLineRef.current.length >= 1) {
          // tambahkan titik terakhir dari double-click
          const next = [...newLineRef.current, e.latlng];
          newLineRef.current = next;
          setNewLine(next);
          tempPolylineRef.current?.setLatLngs(next as any);
          // langsung buka modal simpan
          setEditPipaForm({ id: "", jenis: "Pipa 100", keterangan: "" });
          setShowEditPipaModal(true);
          setModeInfo("Isi detail pipa baru");
        }
      }
      if (currentMode === "measure") {
        setMode("");
        modeRef.current = "";
        setModeInfo("");
      }
    });

    renderMarkers(komponenList);
    renderPipa(pipaFeatures);
    setLoading(false);

    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => { renderMarkers(komponenList); }, [komponenList, renderMarkers]);
  useEffect(() => { renderPipa(pipaFeatures); }, [pipaFeatures, renderPipa]);

  const resetMode = () => {
    setMode(""); modeRef.current = ""; setModeInfo(""); setNewLine([]); newLineRef.current = []; tempPolylineRef.current?.setLatLngs([]); setMeasurePoints([]);
    setStreetViewActive(false);
    if (mapRef.current) mapRef.current.getContainer().style.cursor = "";
    if (editableLayerRef.current) {
      try { editableLayerRef.current.disableEdit?.(); } catch {}
      const info = PIPE_COLORS[editableLayerRef.current._jenisPipa] || { weight: 3 } as any;
      editableLayerRef.current.setStyle({ weight: info.weight, dashArray: null });
      editableLayerRef.current = null;
    }
  };

  const handleSaveMarker = () => {
    const newItem: KomponenTeknik = {
      id: Date.now().toString(),
      lat: addMarkerForm.lat,
      lng: addMarkerForm.lng,
      kategori: addMarkerForm.kategori,
      keterangan: addMarkerForm.keterangan,
    };
    setKomponenList(prev => [...prev, newItem]);
    setShowAddMarkerModal(false);
    resetMode();
    Swal.fire({ icon: "success", title: "Marker ditambah" });
  };

  const handleSaveNewPipa = () => {
    const coords = newLineRef.current.map(p => [p.lng, p.lat] as [number, number]);
    if (coords.length < 2) {
      Swal.fire({ icon: "warning", title: "Minimal 2 titik" });
      return;
    }
    const newFeature: PipaFeature = {
      type: "Feature",
      properties: { id: Date.now(), jenis_pipa: editPipaForm.jenis, keterangan: editPipaForm.keterangan },
      geometry: { type: "LineString", coordinates: coords },
    };
    setPipaFeatures(prev => [...prev, newFeature]);
    setShowEditPipaModal(false);
    setNewLine([]); newLineRef.current = []; tempPolylineRef.current?.setLatLngs([]); resetMode();
    Swal.fire({ icon: "success", title: "Pipa ditambah" });
  };

  const handleUpdatePipa = () => {
    const layer = editableLayerRef.current;
    if (!layer) return;
    const latlngs = layer.getLatLngs() as L.LatLng[];
    const coords = latlngs.map(c => [c.lng, c.lat] as [number, number]);
    setPipaFeatures(prev => prev.map(f => f.properties.id === Number(editPipaForm.id) ? {
      ...f,
      properties: { ...f.properties, jenis_pipa: editPipaForm.jenis, keterangan: editPipaForm.keterangan },
      geometry: { ...f.geometry, coordinates: coords }
    } : f));
    setShowEditPipaModal(false);
    resetMode();
    Swal.fire({ icon: "success", title: "Pipa diupdate" });
  };

  const switchBasemap = (key: BasemapKey) => {
    if (!mapRef.current) return;
    if (basemapLayerRef.current) mapRef.current.removeLayer(basemapLayerRef.current);
    basemapLayerRef.current = L.tileLayer(BASEMAPS[key].url, BASEMAPS[key].opts).addTo(mapRef.current);
    setCurrentBasemap(key);
  };

  const exportMap = async () => {
    if (!mapDivRef.current) return;
    setShowExport(false); setLoading(true);
    try {
      const canvas = await html2canvas(mapDivRef.current, { useCORS: true, scale: 2 });
      const link = document.createElement("a");
      link.download = `peta-admin-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } finally { setLoading(false); }
  };

  const totalMeasure = measurePoints.length >=2 ? calcDistance(measurePoints as any) : 0;

  return (
    <div className="admin-gis-root">
      {loading && <div className="loading-overlay"><div className="spinner-custom"></div><div>Memuat Peta Admin</div></div>}
      
      {/* Street View Container - sesuai referensi HTML */}
      <div id="street-view-container" style={{ 
        display: showStreetView ? "block" : "none",
        position: "fixed", bottom: 0, right: 0, width: "50%", height: "50%",
        zIndex: 2000, background: "white", boxShadow: "0 -2px 20px rgba(0,0,0,0.3)",
        borderTopLeftRadius: 12, overflow: "hidden"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", background: "#1e88e5", color: "white" }}>
          <span><i className="bi bi-geo-alt"></i> Street View</span>
          <button onClick={closeStreetView} style={{ background: "transparent", border: "none", color: "white", fontSize: 18, cursor: "pointer" }}>×</button>
        </div>
        <div id="street-view" style={{ width: "100%", height: "calc(100% - 36px)" }}></div>
      </div>

      {/* <nav className="navbar-custom">
        <div className="navbar-brand"><i className="bi bi-pencil-fill"></i><span>Admin GIS</span></div>
        <div className="navbar-menu">
          <a href="/dashboard.php" className="nav-item"><i className="bi bi-house-door"></i> Dashboard</a>
          <a href="/gisteknik.php" className="nav-item"><i className="bi bi-diagram-3"></i> GIS Teknik</a>
          <div className="user-info"><div className="user-avatar">{userName[0]}</div><span>{userName}</span></div>
        </div>
      </nav> */}
      <div className="map-container">
        <div ref={mapDivRef} id="map" style={{ height: "100%", width: "100%" }} />
        <div className="tools-bar">
          <button className={mode==="marker"?"active":""} onClick={()=>{ if(mode==="marker") resetMode(); else { resetMode(); setMode("marker"); modeRef.current="marker"; setModeInfo("Klik di peta untuk tambah marker"); } }}><i className="bi bi-geo-alt"></i> Tambah Marker</button>
          <button className={mode==="pipa"?"active":""} onClick={()=>{ if(mode==="pipa") resetMode(); else { resetMode(); setMode("pipa"); modeRef.current="pipa"; setModeInfo("Klik titik-titik pipa, double-click untuk selesai"); } }}><i className="bi bi-diagram-2"></i> Tambah Pipa</button>
          <button className={mode==="jaringan"?"active":""} onClick={()=>{ if(mode==="jaringan") resetMode(); else { resetMode(); setMode("jaringan"); modeRef.current="jaringan"; setModeInfo("Klik pipa untuk edit vertex"); } }}><i className="bi bi-pencil-square"></i> Edit Pipa</button>
          <button className={mode==="streetview"?"active":""} onClick={toggleStreetView} id="btnStreetView"><i className="bi bi-signpost-split"></i> Street View</button>
          <button className={mode==="measure"?"active":""} onClick={()=>{ if(mode==="measure") resetMode(); else { resetMode(); setMode("measure"); modeRef.current="measure"; } }}><i className="bi bi-rulers"></i> Ukur</button>
          <button className={showLabels?"active":""} onClick={()=>setShowLabels(!showLabels)}><i className="bi bi-tag"></i> Label</button>
          <button onClick={()=>setShowExport(true)}><i className="bi bi-printer"></i> Export</button>
        </div>
        <div className="custom-map-controls">
          <button className="map-control-btn" onClick={()=>mapRef.current?.zoomIn()}><i className="bi bi-plus-lg"></i></button>
          <button className="map-control-btn" onClick={()=>mapRef.current?.zoomOut()}><i className="bi bi-dash-lg"></i></button>
          <button className="map-control-btn" onClick={()=>navigator.geolocation.getCurrentPosition(p=>mapRef.current?.setView([p.coords.latitude,p.coords.longitude],16))}><i className="bi bi-crosshair"></i></button>
        </div>
        <div className="custom-basemap-control">
          {Object.entries(BASEMAPS).map(([k,v])=>(
            <button key={k} className={`basemap-btn ${currentBasemap===k?"active":""}`} onClick={()=>switchBasemap(k as BasemapKey)}>{v.label}</button>
          ))}
        </div>
        <div className="info-panel">
          <div className="info-header"><span>Legenda & Filter</span><span>{stats.marker} | {stats.pipa}</span></div>
          <div className="info-body">
            {modeInfo && <div className="mode-info">{modeInfo} {mode==="pipa" && newLine.length>0 && `(${newLine.length} titik)`}</div>}
            {mode==="measure" && <div className="measure-info">Jarak: {totalMeasure.toFixed(2)} m - Titik: {measurePoints.length}</div>}
            <h6>Komponen ({komponenList.length})</h6>
            {Object.entries(KATEGORI_ICONS).map(([key, icon])=>(
              <div key={key} className="legend-checkbox-item">
                <input type="checkbox" checked={markerVisibility[key]!==false} onChange={e=>setMarkerVisibility(prev=>({...prev, [key]: e.target.checked}))} />
                <span>{icon.html}</span><label>{icon.label}</label>
              </div>
            ))}
            <h6 style={{ marginTop: 12 }}>Jaringan Pipa ({pipaFeatures.length})</h6>
            {Object.entries(PIPE_COLORS).map(([key, style])=>(
              <div key={key} className="legend-checkbox-item">
                <input type="checkbox" checked={pipeVisibility[key]!==false} onChange={e=>setPipeVisibility(prev=>({...prev, [key]: e.target.checked}))} />
                <div className="legend-color" style={{ background: style.color, width: 20, height: style.weight }}></div>
                <label>{style.label}</label>
              </div>
            ))}
            {editableLayerRef.current && mode==="jaringan" && <button className="btn btn-success w-100 mt-2" onClick={handleUpdatePipa}>Simpan Edit Pipa</button>}
            {mode==="pipa" && newLine.length>=2 && <button className="btn btn-primary w-100 mt-2" onClick={()=>{ setEditPipaForm({ id:"", jenis:"Pipa 100", keterangan:""}); setShowEditPipaModal(true); }}>Selesai & Simpan ({newLine.length} titik)</button>}
            {mode==="pipa" && newLine.length>0 && <button className="btn btn-secondary w-100 mt-2" onClick={()=>{ setNewLine([]); newLineRef.current=[]; tempPolylineRef.current?.setLatLngs([]); }}>Reset Garis</button>}
          </div>
        </div>
      </div>

      {showAddMarkerModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h5>Tambah Komponen</h5>
            <p>Lat: {addMarkerForm.lat.toFixed(6)} Lng: {addMarkerForm.lng.toFixed(6)}</p>
            <select value={addMarkerForm.kategori} onChange={e=>setAddMarkerForm({...addMarkerForm, kategori: e.target.value})} className="form-select mb-2">
              {Object.keys(KATEGORI_ICONS).map(k=><option key={k} value={k}>{k}</option>)}
            </select>
            <input placeholder="Keterangan" value={addMarkerForm.keterangan} onChange={e=>setAddMarkerForm({...addMarkerForm, keterangan: e.target.value})} className="form-control mb-2" />
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary" onClick={()=>{ setShowAddMarkerModal(false); resetMode(); }}>Batal</button>
              <button className="btn btn-primary" onClick={handleSaveMarker}>Simpan</button>
            </div>
          </div>
        </div>
      )}

      {showEditPipaModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h5>{editPipaForm.id ? "Edit Pipa" : "Tambah Pipa Baru"} {newLine.length>0 && `(${newLine.length} titik, ${calcDistance(newLine as any).toFixed(1)}m)`}</h5>
            <select value={editPipaForm.jenis} onChange={e=>setEditPipaForm({...editPipaForm, jenis: e.target.value})} className="form-select mb-2">
              {Object.keys(PIPE_COLORS).map(k=><option key={k} value={k}>{k}</option>)}
            </select>
            <input placeholder="Keterangan" value={editPipaForm.keterangan} onChange={e=>setEditPipaForm({...editPipaForm, keterangan: e.target.value})} className="form-control mb-3" />
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary" onClick={()=>{ setShowEditPipaModal(false); if(!editPipaForm.id){ setNewLine([]); newLineRef.current=[]; tempPolylineRef.current?.setLatLngs([]);} }}>Batal</button>
              {editPipaForm.id ? <button className="btn btn-primary" onClick={handleUpdatePipa}>Update</button> : <button className="btn btn-primary" onClick={handleSaveNewPipa}>Simpan Pipa</button>}
            </div>
          </div>
        </div>
      )}

      {showExport && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h5>Export Peta</h5>
            <button className="btn btn-primary w-100" onClick={exportMap}>Export PNG</button>
            <button className="btn btn-secondary w-100 mt-2" onClick={()=>setShowExport(false)}>Tutup</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeknikPetaJaringan;
